import { Component, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { FileUploadModule } from '@copo/file-upload-new';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgFor } from '@angular/common';

import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { StepClickedEvent, StepStatus, StepperComponent }
	from '../../../../shared/components/stepper/stepper.component';
import { User } from '../../../../shared/models/user';
import { LocationService } from '../../../../shared/services/location.service';
import { TranslationService } from '../../../../shared/services/translation.service';
import { UserService } from '../../../../shared/services/user.service';
import { Guarantee } from '../../../models/guarantee';
import { GuaranteeRequestService } from '../../../services/guarantee-request.service';
import { ATTACHMENT_CATEGORY_SOIL, ATTACHMENT_CATEGORY_WATER, GuaranteeStatus, PartForm }
	from '../../../models/guarantee-constants';
import { GuaranteeRequestAttachment } from '../../../models/guarantee-request-attachment';
import { Part } from '../../../models/part';
import { CORROSION_OPTIONS, CORROSION_OPTIONS_KEYS, EPASSIVE_OPTIONS, EPASSIVE_OPTIONS_KEYS,
	FABRICATION_OPTIONS, FABRICATION_OPTIONS_KEYS } from '../../../../shared/models/list';
import { safe } from '../../../../shared/utils/safe';
import { SnackbarSaveDraftGuaranteeComponent } from '../../utils/snackbar-save-draft-guarantee.component';
import { FeedbackErrorComponent } from '../../../../shared/components/feedback-error/feedback-error.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { InputNumberDirective } from '../../../../shared/directives/input-number.directive';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { SafeUrlPipe } from '../../../../shared/pipes/safeurl.pipe';

import { buildAttachments, formatAttachmentSize } from './type-of-guarantee.helpers';
import { GuaranteeRequestAttachmentEditor } from './type-of-guarantee-attachment-editor';

const corrosionTypeValues = CORROSION_OPTIONS_KEYS.map(key => ({ key, value: CORROSION_OPTIONS[key] }));
const fabricationProcessValues = FABRICATION_OPTIONS_KEYS.map(key => ({ key, value: FABRICATION_OPTIONS[key] }));
const epassiveValues = EPASSIVE_OPTIONS_KEYS.map(key => ({ key, value: EPASSIVE_OPTIONS[key] }));
const fabricationProcessOtherValues = FABRICATION_OPTIONS_KEYS.slice(0, 2)
	.map(key => ({ key, value: FABRICATION_OPTIONS[key] }));
@Component({
	selector: 'copo-type-of-guarantee',
	templateUrl: './type-of-guarantee.component.html',
	styleUrl: './type-of-guarantee.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		CdkScrollable,
		PageHeaderComponent,
		MatButtonModule,
		TranslateModule,
		MatIconModule,
		FormsModule,
		ReactiveFormsModule,
		StepperComponent,
		MatCheckboxModule,
		MatFormFieldModule,
		MatInputModule,
		InputNumberDirective,
		MatRadioModule,
		FileUploadModule,
		NgFor,
		MatSelectModule,
		MatOptionModule,
		MatTooltipModule,
		TextFieldModule,
		LoadingSpinnerComponent,
		SafeUrlPipe,
		FeedbackErrorComponent
	]
})
export class TypeOfGuaranteeComponent {
	submitting = false;
	readonly attachmentEditor: GuaranteeRequestAttachmentEditor;

	guarantee: Guarantee;
	subMenuDisplayed = false;
	user: User;
	hashInProgress = false;
	partsToSend: Part[] = [];
	attachmentError = '';
	readonly steps;

	magnelisZM310FormControl: FormControl<boolean>;
	magnelisZM430FormControl: FormControl<boolean>;
	magnelisZM620FormControl: FormControl<boolean>;
	guaranteeDurationFormControl: FormControl<string>;
	upperStructureGuaranteeFormControl: FormControl<boolean>;
	polesMagnelisZM430FormControl: FormControl<boolean>;
	polesMagnelisZM620FormControl: FormControl<boolean>;
	polesMagnelisZM800FormControl: FormControl<string>;
	poleGuaranteeDurationFormControl: FormControl<string>;
	entireMagnelisFormControl: FormControl<boolean>;
	stainlessSteelPartsFormControl: FormControl<boolean>;
	aluminiumPartsFormControl: FormControl<boolean>;
	foundationsConcreteFormControl: FormControl<boolean>;
	stainlessSteelFormControl: FormControl<boolean>;
	polesGuaranteeFormControl: FormControl<boolean>;
	floatingSolarMagnelisZM430FormControl: FormControl<string>;
	floatingSolarMagnelisZM620FormControl: FormControl<string>;
	floatingSolarMagnelisZM800FormControl: FormControl<string>;
	floatingPoleGuaranteeDurationFormControl: FormControl<string>;
	guaranteeForFloatingSolarMagnelisFormControl: FormControl<string>;
	contactPartsFormControl: FormControl<boolean>;
	contactPartsOtherFormControl: FormControl<boolean>;
	zincCoatedSteelFormControl: FormControl<boolean>;
	otherCoatingsSteelFormControl: FormControl<boolean>;
	magnelisContactPartsCoatingFormControl: FormControl<string>;
	aluminiumFormControl: FormControl<boolean>;
	soilAnalysisFormControl: FormControl<boolean>;
	floatingSolarInstallationAnalysisFormControl: FormControl<string>;
	magnelisContactPartsOtherMaterialFormControl: FormControl<string>;
	customerAdditionalInformationFormControl: FormControl<string>;
	partsForm: FormGroup<{
		parts: FormArray<FormGroup<PartForm>>;
	}>;

	readonly corrosionTypeValues = corrosionTypeValues;
	readonly fabricationProcessValues = fabricationProcessValues;
	readonly fabricationProcessOtherValues = fabricationProcessOtherValues;
	readonly epassiveValues = epassiveValues;
	typeOfGuaGrOutdoorFormControl: FormControl<boolean>;
	typeofGuaGrIndoorFormControl: FormControl<boolean>;
	outdoorGuaranteeDurationFormControl: FormControl<string>;
	indoorGuaranteeDurationFormControl: FormControl<string>;
	typeOfGUAOpenBuildingYesFormControl: FormControl<boolean>;
	typeOfGUAOpenBuildingYesSpecifyFormControl: FormControl<string>;
	ocRoofingFormControl: FormControl<boolean>;
	roofingDeckingRoofFormControl: FormControl<boolean>;
	roofingDRSubstrateThicknessFormControl: FormControl<string>;
	roofingDRDesiredCoatingFormControl: FormControl<string>;
	roofingDRDesiredBackCoatingFormControl: FormControl<string>;
	roofingDRMetallicCoatingWeightFormControl: FormControl<string>;
	roofingDRColorFormControl: FormControl<string>;
	roofingDRSurfaceFormControl: FormControl<string>;
	roofingDRRoofslopeFormControl: FormControl<string>;
	roofingSingleSkinRoofFormControl: FormControl<boolean>;
	roofingSSRSubstrateThicknessFormControl: FormControl<string>;
	roofingSSRDesiredCoatingFormControl: FormControl<string>;
	roofingSSRDesiredBackCoatingFormControl: FormControl<string>;
	roofingSSRMetallicCoatingWeightFormControl: FormControl<string>;
	roofingSSRColorFormControl: FormControl<string>;
	roofingSSRSurfaceFormControl: FormControl<string>;
	roofingSSRRoofslopeFormControl: FormControl<string>;
	roofingDoubleSkinRoofFormControl: FormControl<boolean>;
	roofingDsresSubstrateThicknessFormControl: FormControl<string>;
	roofingDsresDesiredCoatingFormControl: FormControl<string>;
	roofingDsresDesiredBackCoatingFormControl: FormControl<string>;
	roofingDsresMetallicCoatingWeightFormControl: FormControl<string>;
	roofingDsrEsColorFormControl: FormControl<string>;
	roofingDsrisSubstrateThicknessFormControl: FormControl<string>;
	roofingDsrisDesiredCoatingFormControl: FormControl<string>;
	roofingDsrisDesiredBackCoatingFormControl: FormControl<string>;
	roofingDsrisMetallicCoatingWeightFormControl: FormControl<string>;
	roofingDsrIsColorFormControl: FormControl<string>;
	roofingDSRSurfaceFormControl: FormControl<string>;
	roofingDSRRoofslopeFormControl: FormControl<string>;
	roofingSandwichPanelsBuiltUpSysFormControl: FormControl<boolean>;
	roofingSPESSubstrateThicknessFormControl: FormControl<string>;
	roofingSPESDesiredCoatingFormControl: FormControl<string>;
	roofingSPESDesiredBackCoatingFormControl: FormControl<string>;
	roofingSPESMetallicCoatingWeightFormControl: FormControl<string>;
	roofingSPESColorFormControl: FormControl<string>;
	roofingSPISSubstrateThicknessFormControl: FormControl<string>;
	roofingSPISDesiredCoatingFormControl: FormControl<string>;
	roofingSPISDesiredBackCoatingFormControl: FormControl<string>;
	roofingSPISMetallicCoatingWeightFormControl: FormControl<string>;
	roofingSPISColorFormControl: FormControl<string>;
	roofingSPSurfaceFormControl: FormControl<string>;
	roofingSPRoofslopeFormControl: FormControl<string>;
	roofingSPInsulationPUFormControl: FormControl<boolean>;
	roofingSPInsulationPIRFormControl: FormControl<boolean>;
	roofingSPInsulationMineralWoolFormControl: FormControl<boolean>;
	roofingOverlappingFormControl: FormControl<string>;
	roofingPerforationYesFormControl: FormControl<string>;
	roofingPerforationYesSpecifyFormControl: FormControl<string>;
	roofingPVSolarRoofFormControl: FormControl<string>;
	roofingAntiCondensationFeltFormControl: FormControl<string>;
	cladFormControl: FormControl<boolean>;
	cladSingleSkinFormControl: FormControl<boolean>;
	cladSSSubstrateThicknessFormControl: FormControl<string>;
	cladSSDesiredCoatingFormControl: FormControl<string>;
	cladSSDesiredBackCoatingFormControl: FormControl<string>;
	cladSSMetallicCoatingWeightFormControl: FormControl<string>;
	cladSSColorFormControl: FormControl<string>;
	cladSSSurfaceFormControl: FormControl<string>;
	cladDoubleSkinFormControl: FormControl<boolean>;
	cladDSESSubstrateThicknessFormControl: FormControl<string>;
	cladDSESDesiredCoatingFormControl: FormControl<string>;
	cladDSESDesiredBackCoatingFormControl: FormControl<string>;
	cladDSESMetallicCoatingWeightFormControl: FormControl<string>;
	cladDSESColorFormControl: FormControl<string>;
	cladDSESSurfaceFormControl: FormControl<string>;
	cladDSISSubstrateThicknessFormControl: FormControl<string>;
	cladDSISDesiredCoatingFormControl: FormControl<string>;
	cladDSISDesiredBackCoatingFormControl: FormControl<string>;
	cladDSISMetallicCoatingWeightFormControl: FormControl<string>;
	cladDSISColorFormControl: FormControl<string>;
	cladDSISSurfaceFormControl: FormControl<string>;
	cladSandwichPanelsBuiltUpSysFormControl: FormControl<boolean>;
	cladSPESSubstrateThicknessFormControl: FormControl<string>;
	cladSPESDesiredCoatingFormControl: FormControl<string>;
	cladSPESDesiredBackCoatingFormControl: FormControl<string>;
	cladSPESMetallicCoatingWeightFormControl: FormControl<string>;
	cladSPESColorFormControl: FormControl<string>;
	cladSPISSubstrateThicknessFormControl: FormControl<string>;
	cladSPISDesiredCoatingFormControl: FormControl<string>;
	cladSPISDesiredBackCoatingFormControl: FormControl<string>;
	cladSPISMetallicCoatingWeightFormControl: FormControl<string>;
	cladSPISColorFormControl: FormControl<string>;
	cladSPSurfaceFormControl: FormControl<string>;
	cladSPInsulationPUFormControl: FormControl<boolean>;
	cladSPInsulationPIRFormControl: FormControl<boolean>;
	cladSPInsulationMineralWoolFormControl: FormControl<boolean>;
	cladOverlappingFormControl: FormControl<string>;
	cladPerforationYesFormControl: FormControl<string>;
	cladPerforationYesSpecifyFormControl: FormControl<string>;
	cladDirectionOfInstallationFormControl: FormControl<boolean>;
	cladVerticalFormControl: FormControl<boolean>;
	othersAntiCondensationFeltFormControl: FormControl<boolean>;
	othersSpecifyAntiCondensationFeltFormControl: FormControl<string>;
	omuRoofCladmadeWithProductFormControl: FormControl<boolean>;
	omuUseofMaterialCementBreezeblocksFormControl: FormControl<boolean>;
	omuInRoofingFormControl: FormControl<string>;
	omuInCladdingFormControl: FormControl<string>;
	suggestedCoatingMassFormControl: FormControl<boolean>;
	magnelisOtherFormControl: FormControl<boolean>;
	guaranteeCoatingMassFormControl: FormControl<string>;

	constructor(
		private readonly locationService: LocationService,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly guaranteeService: GuaranteeRequestService,
		private readonly fb: FormBuilder,
		private readonly snackBar: MatSnackBar,
	) {
		this.steps = [
			{
				label: this.translationService.getTR('guarantee.project.characteristics'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('guarantee.project.environment'),
				status: StepStatus.ENABLED,
			},
			{
				label: this.translationService.getTR('guarantee.type'),
				status: StepStatus.CURRENT,
			},
			{
				label: this.translationService.getTR('guarantee.summary'),
				status: StepStatus.DISABLED,
			}
		];
		const res = <{ guarantee: Guarantee }> this.route.snapshot.data;
		this.guarantee = res['guarantee'];
		this.attachmentEditor = new GuaranteeRequestAttachmentEditor();

		this.user = this.userService.user;
		this.magnelisZM310FormControl = new FormControl(this.guarantee.magnelisZM310, { nonNullable: true });
		this.magnelisZM430FormControl = new FormControl(this.guarantee.magnelisZM430, { nonNullable: true });
		this.magnelisZM620FormControl = new FormControl(this.guarantee.magnelisZM620, { nonNullable: true });
		this.guaranteeDurationFormControl = new FormControl(
			this.guarantee.guaranteeDurationRequested,
			{ nonNullable: true }
		);
		this.upperStructureGuaranteeFormControl = new FormControl(
			this.guarantee.upperStructureGuarantee,
			{ nonNullable: true }
		);
		this.polesMagnelisZM430FormControl = new FormControl(this.guarantee.polesMagnelisZM430, { nonNullable: true });
		this.polesMagnelisZM620FormControl = new FormControl(this.guarantee.polesMagnelisZM620, { nonNullable: true });
		this.polesMagnelisZM800FormControl = new FormControl(
			this.guarantee.polesMagnelisZM800 === 'Yes' ? 'Yes' : '',
			{ nonNullable: true }
		);
		this.poleGuaranteeDurationFormControl = new FormControl(
			this.guarantee.poleGuaranteeDurationRequested,
			{ nonNullable: true }
		);
		this.floatingSolarMagnelisZM430FormControl = new FormControl(
			this.guarantee.floatingSolarMagnelisZM430 === 'Yes' ? 'Yes' : '',
			{ nonNullable: true }
		);
		this.floatingSolarMagnelisZM620FormControl = new FormControl(
			this.guarantee.floatingSolarMagnelisZM620 === 'Yes' ? 'Yes' : '',
			{ nonNullable: true }
		);
		this.floatingSolarMagnelisZM800FormControl = new FormControl(
			this.guarantee.floatingSolarMagnelisZM800 === 'Yes' ? 'Yes' : '',
			{ nonNullable: true }
		);
		this.floatingPoleGuaranteeDurationFormControl = new FormControl(
			this.guarantee.guaranteeDurationRequestedForFoating,
			{ nonNullable: true }
		);
		this.entireMagnelisFormControl = new FormControl(this.guarantee.entireMagnelisStructure, { nonNullable: true });
		this.stainlessSteelPartsFormControl = new FormControl(
			this.guarantee.stainlessSteelParts,
			{ nonNullable: true }
		);
		this.aluminiumPartsFormControl = new FormControl(this.guarantee.aluminiumParts, { nonNullable: true });
		this.foundationsConcreteFormControl = new FormControl(
			this.guarantee.foundationsConcrete,
			{ nonNullable: true }
		);
		this.stainlessSteelFormControl = new FormControl(this.guarantee.stainlessSteel, { nonNullable: true });
		this.suggestedCoatingMassFormControl = new FormControl(
			this.guarantee.suggestedCoatingMass,
			{ nonNullable: true }
		);
		this.contactPartsFormControl = new FormControl(this.guarantee.contactParts, { nonNullable: true });
		this.contactPartsOtherFormControl = new FormControl(this.guarantee.contactOtherParts, { nonNullable: true });
		this.zincCoatedSteelFormControl = new FormControl(this.guarantee.zincCoated, { nonNullable: true });
		this.otherCoatingsSteelFormControl = new FormControl(this.guarantee.otherCoatingsSteel, { nonNullable: true });
		this.soilAnalysisFormControl = new FormControl(this.guarantee.soilAnalysis, { nonNullable: true });
		this.floatingSolarInstallationAnalysisFormControl = new FormControl(
			this.guarantee.floatingSolarInstallationAnalysis,
			{ nonNullable: true }
		);
		this.aluminiumFormControl = new FormControl(this.guarantee.aluminium, { nonNullable: true });
		this.polesGuaranteeFormControl = new FormControl(this.guarantee.polesGuarantee, { nonNullable: true });
		this.guaranteeForFloatingSolarMagnelisFormControl = new FormControl(
			this.guarantee.guaranteeForFloatingSolarMagnelis,
			{ nonNullable: true }
		);
		this.suggestedCoatingMassFormControl = new FormControl(
			this.guarantee.suggestedCoatingMass,
			{ nonNullable: true }
		);
		this.magnelisOtherFormControl = new FormControl(this.guarantee.isOtherCoatingMass, { nonNullable: true });
		this.guaranteeCoatingMassFormControl = new FormControl(this.guarantee.otherCoatingMass, { nonNullable: true });
		this.magnelisContactPartsCoatingFormControl = new FormControl(
			this.guarantee.magnelisContactPartsCoating,
			{ nonNullable: true }
		);
		this.magnelisContactPartsOtherMaterialFormControl = new FormControl(
			this.guarantee.magnelisContactPartsOtherMaterial,
			{ nonNullable: true }
		);
		this.customerAdditionalInformationFormControl = new FormControl(
			this.guarantee.customerAdditionalInformation,
			{ nonNullable: true }
		);
		this.partsForm = this.fb.group({
			parts: this.fb.array<FormGroup<PartForm>>([]),
		});
		if (this.guarantee.parts.length === 0) {
			this.guarantee.parts.push(new Part());
		}
		for (const part of this.guarantee.parts) {
			this.addPart(part);
		}
		this.typeOfGuaGrOutdoorFormControl = new FormControl(this.guarantee.typeOfGuaGrOutdoor, { nonNullable: true });
		this.typeofGuaGrIndoorFormControl = new FormControl(this.guarantee.typeofGuaGrIndoor, { nonNullable: true });
		this.outdoorGuaranteeDurationFormControl = new FormControl(
			this.guarantee.outdoorGuaranteeDurationExpected,
			{ nonNullable: true }
		);
		this.indoorGuaranteeDurationFormControl = new FormControl(
			this.guarantee.indoorGuaranteeDurationExpected,
			{ nonNullable: true }
		);
		this.typeOfGUAOpenBuildingYesFormControl = new FormControl(
			this.guarantee.typeOfGUAOpenBuildingYes,
			{ nonNullable: true }
		);
		this.typeOfGUAOpenBuildingYesSpecifyFormControl = new FormControl(
			this.guarantee.typeOfGUAOpenBuildingYesSpecify,
			{ nonNullable: true }
		);
		this.ocRoofingFormControl = new FormControl(this.guarantee.ocRoofing, { nonNullable: true });
		this.roofingDeckingRoofFormControl = new FormControl(this.guarantee.roofingDeckingRoof, { nonNullable: true });
		this.roofingDRSubstrateThicknessFormControl = new FormControl(
			this.guarantee.roofingDRSubstrateThickness,
			{ nonNullable: true }
		);
		this.roofingDRDesiredCoatingFormControl = new FormControl(
			this.guarantee.roofingDRDesiredCoating,
			{ nonNullable: true }
		);
		this.roofingDRDesiredBackCoatingFormControl = new FormControl(
			this.guarantee.roofingDRDesiredBackCoating,
			{ nonNullable: true }
		);
		this.roofingDRMetallicCoatingWeightFormControl = new FormControl(
			this.guarantee.roofingDRMetallicCoatingWeight,
			{ nonNullable: true }
		);
		this.roofingDRColorFormControl = new FormControl(this.guarantee.roofingDRColor, { nonNullable: true });
		this.roofingDRSurfaceFormControl = new FormControl(this.guarantee.roofingDRSurface, { nonNullable: true });
		this.roofingDRRoofslopeFormControl = new FormControl(this.guarantee.roofingDRRoofslope, { nonNullable: true });
		this.roofingSingleSkinRoofFormControl = new FormControl(
			this.guarantee.roofingSingleSkinRoof,
			{ nonNullable: true }
		);
		this.roofingSSRSubstrateThicknessFormControl = new FormControl(
			this.guarantee.roofingSSRSubstrateThickness,
			{ nonNullable: true }
		);
		this.roofingSSRDesiredCoatingFormControl = new FormControl(
			this.guarantee.roofingSSRDesiredCoating,
			{ nonNullable: true }
		);
		this.roofingSSRDesiredBackCoatingFormControl = new FormControl(
			this.guarantee.roofingSSRDesiredBackCoating,
			{ nonNullable: true }
		);
		this.roofingSSRMetallicCoatingWeightFormControl = new FormControl(
			this.guarantee.roofingSSRMetallicCoatingWeight,
			{ nonNullable: true }
		);
		this.roofingSSRColorFormControl = new FormControl(this.guarantee.roofingSSRColor, { nonNullable: true });
		this.roofingSSRSurfaceFormControl = new FormControl(this.guarantee.roofingSSRSurface, { nonNullable: true });
		this.roofingSSRRoofslopeFormControl = new FormControl(
			this.guarantee.roofingSSRRoofslope,
			{ nonNullable: true }
		);
		this.roofingDoubleSkinRoofFormControl = new FormControl(
			this.guarantee.roofingDoubleSkinRoof,
			{ nonNullable: true }
		);
		this.roofingDsresSubstrateThicknessFormControl = new FormControl(
			this.guarantee.roofingDsresSubstrateThickness,
			{ nonNullable: true }
		);
		this.roofingDsresDesiredCoatingFormControl = new FormControl(
			this.guarantee.roofingDsresDesiredCoating,
			{ nonNullable: true }
		);
		this.roofingDsresDesiredBackCoatingFormControl = new FormControl(
			this.guarantee.roofingDsresDesiredBackCoating,
			{ nonNullable: true }
		);
		this.roofingDsresMetallicCoatingWeightFormControl = new FormControl(
			this.guarantee.roofingDsresMetallicCoatingWeight,
			{ nonNullable: true }
		);
		this.roofingDsrEsColorFormControl = new FormControl(this.guarantee.roofingDsrEsColor, { nonNullable: true });
		this.roofingDsrisSubstrateThicknessFormControl = new FormControl(
			this.guarantee.roofingDsrisSubstrateThickness,
			{ nonNullable: true }
		);
		this.roofingDsrisDesiredCoatingFormControl = new FormControl(
			this.guarantee.roofingDsrisDesiredCoating,
			{ nonNullable: true }
		);
		this.roofingDsrisDesiredBackCoatingFormControl = new FormControl(
			this.guarantee.roofingDsrisDesiredBackCoating,
			{ nonNullable: true }
		);
		this.roofingDsrisMetallicCoatingWeightFormControl = new FormControl(
			this.guarantee.roofingDsrisMetallicCoatingWeight,
			{ nonNullable: true }
		);
		this.roofingDsrIsColorFormControl = new FormControl(this.guarantee.roofingDsrIsColor, { nonNullable: true });
		this.roofingDSRSurfaceFormControl = new FormControl(this.guarantee.roofingDSRSurface, { nonNullable: true });
		this.roofingDSRRoofslopeFormControl = new FormControl(
			this.guarantee.roofingDSRRoofslope,
			{ nonNullable: true }
		);
		this.roofingSandwichPanelsBuiltUpSysFormControl = new FormControl(
			this.guarantee.roofingSandwichPanelsBuiltUpSys,
			{ nonNullable: true }
		);
		this.roofingSPESSubstrateThicknessFormControl = new FormControl(
			this.guarantee.roofingSPESSubstrateThickness,
			{ nonNullable: true }
		);
		this.roofingSPESDesiredCoatingFormControl = new FormControl(
			this.guarantee.roofingSPESDesiredCoating,
			{ nonNullable: true }
		);
		this.roofingSPESDesiredBackCoatingFormControl = new FormControl(
			this.guarantee.roofingSPESDesiredBackCoating,
			{ nonNullable: true }
		);
		this.roofingSPESMetallicCoatingWeightFormControl = new FormControl(
			this.guarantee.roofingSPESMetallicCoatingWeight,
			{ nonNullable: true }
		);
		this.roofingSPESColorFormControl = new FormControl(this.guarantee.roofingSPESColor, { nonNullable: true });
		this.roofingSPISSubstrateThicknessFormControl = new FormControl(
			this.guarantee.roofingSPISSubstrateThickness,
			{ nonNullable: true }
		);
		this.roofingSPISDesiredCoatingFormControl = new FormControl(
			this.guarantee.roofingSPISDesiredCoating,
			{ nonNullable: true }
		);
		this.roofingSPISDesiredBackCoatingFormControl = new FormControl(
			this.guarantee.roofingSPISDesiredBackCoating,
			{ nonNullable: true }
		);
		this.roofingSPISMetallicCoatingWeightFormControl = new FormControl(
			this.guarantee.roofingSPISMetallicCoatingWeight,
			{ nonNullable: true }
		);
		this.roofingSPISColorFormControl = new FormControl(this.guarantee.roofingSPISColor, { nonNullable: true });
		this.roofingSPSurfaceFormControl = new FormControl(this.guarantee.roofingSPSurface, { nonNullable: true });
		this.roofingSPRoofslopeFormControl = new FormControl(this.guarantee.roofingSPRoofslope, { nonNullable: true });
		this.roofingSPInsulationPUFormControl = new FormControl(
			this.guarantee.roofingSPInsulationPU,
			{ nonNullable: true }
		);
		this.roofingSPInsulationPIRFormControl = new FormControl(
			this.guarantee.roofingSPInsulationPIR,
			{ nonNullable: true }
		);
		this.roofingSPInsulationMineralWoolFormControl = new FormControl(
			this.guarantee.roofingSPInsulationMineralWool,
			{ nonNullable: true }
		);
		this.roofingOverlappingFormControl = new FormControl(this.guarantee.roofingOverlapping, { nonNullable: true });
		this.roofingPerforationYesFormControl = new FormControl(
			this.guarantee.roofingPerforationYes,
			{ nonNullable: true }
		);
		this.roofingPerforationYesSpecifyFormControl = new FormControl(
			this.guarantee.roofingPerforationYesSpecify,
			{ nonNullable: true }
		);
		this.roofingPVSolarRoofFormControl = new FormControl(
			this.guarantee.roofingPVSolarRoof,
			{ nonNullable: true }
		);
		this.roofingAntiCondensationFeltFormControl = new FormControl(
			this.guarantee.roofingAntiCondensationFelt,
			{ nonNullable: true }
		);
		this.cladFormControl = new FormControl(this.guarantee.clad, { nonNullable: true });
		this.cladSingleSkinFormControl = new FormControl(this.guarantee.cladSingleSkin, { nonNullable: true });
		this.cladSSSubstrateThicknessFormControl = new FormControl(
			this.guarantee.cladSSSubstrateThickness,
			{ nonNullable: true }
		);
		this.cladSSDesiredCoatingFormControl = new FormControl(
			this.guarantee.cladSSDesiredCoating,
			{ nonNullable: true }
		);
		this.cladSSDesiredBackCoatingFormControl = new FormControl(
			this.guarantee.cladSSDesiredBackCoating,
			{ nonNullable: true }
		);
		this.cladSSMetallicCoatingWeightFormControl = new FormControl(
			this.guarantee.cladSSMetallicCoatingWeight,
			{ nonNullable: true }
		);
		this.cladSSColorFormControl = new FormControl(this.guarantee.cladSSColor, { nonNullable: true });
		this.cladSSSurfaceFormControl = new FormControl(this.guarantee.cladSSSurface, { nonNullable: true });
		this.cladDoubleSkinFormControl = new FormControl(this.guarantee.cladDoubleSkin, { nonNullable: true });
		this.cladDSESSubstrateThicknessFormControl = new FormControl(
			this.guarantee.cladDSESSubstrateThickness,
			{ nonNullable: true }
		);
		this.cladDSESDesiredCoatingFormControl = new FormControl(
			this.guarantee.cladDSESDesiredCoating,
			{ nonNullable: true }
		);
		this.cladDSESDesiredBackCoatingFormControl = new FormControl(
			this.guarantee.cladDSESDesiredBackCoating,
			{ nonNullable: true }
		);
		this.cladDSESMetallicCoatingWeightFormControl = new FormControl(
			this.guarantee.cladDSESMetallicCoatingWeight,
			{ nonNullable: true }
		);
		this.cladDSESColorFormControl = new FormControl(this.guarantee.cladDSESColor, { nonNullable: true });
		this.cladDSESSurfaceFormControl = new FormControl(this.guarantee.cladDSESSurface, { nonNullable: true });
		this.cladDSISSubstrateThicknessFormControl = new FormControl(
			this.guarantee.cladDSISSubstrateThickness,
			{ nonNullable: true }
		);
		this.cladDSISDesiredCoatingFormControl = new FormControl(
			this.guarantee.cladDSISDesiredCoating,
			{ nonNullable: true }
		);
		this.cladDSISDesiredBackCoatingFormControl = new FormControl(
			this.guarantee.cladDSISDesiredBackCoating,
			{ nonNullable: true }
		);
		this.cladDSISMetallicCoatingWeightFormControl = new FormControl(
			this.guarantee.cladDSISMetallicCoatingWeight,
			{ nonNullable: true }
		);
		this.cladDSISColorFormControl = new FormControl(this.guarantee.cladDSISColor, { nonNullable: true });
		this.cladDSISSurfaceFormControl = new FormControl(this.guarantee.cladDSISSurface, { nonNullable: true });
		this.cladSandwichPanelsBuiltUpSysFormControl = new FormControl(
			this.guarantee.cladSandwichPanelsBuiltUpSys,
			{ nonNullable: true }
		);
		this.cladSPESSubstrateThicknessFormControl = new FormControl(
			this.guarantee.cladSPESSubstrateThickness,
			{ nonNullable: true }
		);
		this.cladSPESDesiredCoatingFormControl = new FormControl(
			this.guarantee.cladSPESDesiredCoating,
			{ nonNullable: true }
		);
		this.cladSPESDesiredBackCoatingFormControl = new FormControl(
			this.guarantee.cladSPESDesiredBackCoating,
			{ nonNullable: true }
		);
		this.cladSPESMetallicCoatingWeightFormControl = new FormControl(
			this.guarantee.cladSPESMetallicCoatingWeight,
			{ nonNullable: true }
		);
		this.cladSPESColorFormControl = new FormControl(this.guarantee.cladSPESColor, { nonNullable: true });
		this.cladSPISSubstrateThicknessFormControl = new FormControl(
			this.guarantee.cladSPISSubstrateThickness,
			{ nonNullable: true }
		);
		this.cladSPISDesiredCoatingFormControl = new FormControl(
			this.guarantee.cladSPISDesiredCoating,
			{ nonNullable: true }
		);
		this.cladSPISDesiredBackCoatingFormControl = new FormControl(
			this.guarantee.cladSPISDesiredBackCoating,
			{ nonNullable: true }
		);
		this.cladSPISMetallicCoatingWeightFormControl = new FormControl(
			this.guarantee.cladSPISMetallicCoatingWeight,
			{ nonNullable: true }
		);
		this.cladSPISColorFormControl = new FormControl(this.guarantee.cladSPISColor, { nonNullable: true });
		this.cladSPSurfaceFormControl = new FormControl(this.guarantee.cladSPSurface, { nonNullable: true });
		this.cladSPInsulationPUFormControl = new FormControl(this.guarantee.cladSPInsulationPU, { nonNullable: true });
		this.cladSPInsulationPIRFormControl = new FormControl(
			this.guarantee.cladSPInsulationPIR,
			{ nonNullable: true }
		);
		this.cladSPInsulationMineralWoolFormControl = new FormControl(
			this.guarantee.cladSPInsulationMineralWool,
			{ nonNullable: true }
		);
		this.cladOverlappingFormControl = new FormControl(this.guarantee.cladOverlapping, { nonNullable: true });
		this.cladPerforationYesFormControl = new FormControl(this.guarantee.cladPerforationYes, { nonNullable: true });
		this.cladPerforationYesSpecifyFormControl = new FormControl(
			this.guarantee.cladPerforationYesSpecify,
			{ nonNullable: true }
		);
		this.cladDirectionOfInstallationFormControl = new FormControl(
			this.guarantee.cladDirectionOfInstallation,
			{ nonNullable: true }
		);
		this.cladVerticalFormControl = new FormControl(this.guarantee.cladVertical, { nonNullable: true });
		this.othersAntiCondensationFeltFormControl = new FormControl(
			this.guarantee.othersAntiCondensationFelt,
			{ nonNullable: true }
		);
		this.othersSpecifyAntiCondensationFeltFormControl = new FormControl(
			this.guarantee.othersSpecifyAntiCondensationFelt,
			{ nonNullable: true }
		);
		this.omuRoofCladmadeWithProductFormControl = new FormControl(
			this.guarantee.omuRoofCladmadeWithProduct,
			{ nonNullable: true }
		);
		this.omuUseofMaterialCementBreezeblocksFormControl = new FormControl(
			this.guarantee.omuUseofMaterialCementBreezeblocks,
			{ nonNullable: true }
		);
		this.omuInRoofingFormControl = new FormControl(this.guarantee.omuInRoofing, { nonNullable: true });
		this.omuInCladdingFormControl = new FormControl(this.guarantee.omuInCladding, { nonNullable: true });
	}

	parts() {
		return this.partsForm.controls.parts;
	}

	addPart(part: Part) {
		const fg = this.fb.group({
			number: new FormControl(part.number, { nonNullable: true }),
			name: new FormControl(part.name, { nonNullable: true }),
			sheetThickness: new FormControl(part.sheetThickness, { nonNullable: true }),
			magnelisCoatingMass: new FormControl(part.magnelisCoatingMass, { nonNullable: true }),
			corrosion: new FormControl(part.corrosion, { nonNullable: true }),
			fabricationProcess: new FormControl(part.fabricationProcess, { nonNullable: true }),
			epassivationEasyfilm: new FormControl(part.epassivationEasyfilm, { nonNullable: true }),
		});

		this.parts().push(fg);
	}

	addNewPart() {
		const part = new Part();
		part.number = this.parts().length + 1;
		this.addPart(part);
	}

	removePart(i: number) {
		const partsForm = this.parts();
		partsForm.removeAt(i);
		const parts: Part[] = <Part[]> partsForm.value;
		for (const [index, element] of parts.entries()) {
			element.number = index + 1;
		}
		partsForm.patchValue(parts);
	}

	clearLastPart() {
		this.removePart(0);
		this.addNewPart();
	}

	goBack() {
		this.saveParts();
		this.locationService.go(-1, this.guarantee.id !== undefined
			? `../guarantee-requests/edit/${ this.guarantee.id }/project-environment`
			: '../guarantee-requests/new/project-environment');
	}

	async onStepClicked(event: StepClickedEvent) {
		if (event.stepIndex === 0) {
			await this.router.navigate([
				this.guarantee.id !== undefined
					? `/guarantee-requests/edit/${ this.guarantee.id }/project-characteristics`
					: '/guarantee-requests/new/project-characteristics'
			], { replaceUrl: true });
		}
		if (event.stepIndex === 1) {
			await this.router.navigate([
				this.guarantee.id !== undefined
					? `/guarantee-requests/edit/${ this.guarantee.id }/project-environment`
					: '/guarantee-requests/new/project-environment'
			], { replaceUrl: true });
		}
	}

	toggleSubSave(event: Event) {
		event.preventDefault();
		this.subMenuDisplayed = !this.subMenuDisplayed;
	}

	saveParts() {
		this.partsToSend = <Part[]> this.parts().value;
		for (let i = this.partsToSend.length - 1; i >= 0; i--) {
			if (this.guarantee.applicationType === 'Magnelis-Solar') {
				if (this.partsToSend[i].name === '' && this.partsToSend[i].corrosion === ''
					&& this.partsToSend[i].fabricationProcess === '' && this.partsToSend[i].magnelisCoatingMass === ''
					&& this.partsToSend[i].sheetThickness === '') {
					this.partsToSend.splice(i, 1);
				}
			} else if (this.partsToSend[i].name === ''
				&& this.partsToSend[i].fabricationProcess === '' && this.partsToSend[i].magnelisCoatingMass === ''
				&& this.partsToSend[i].sheetThickness === '' && this.partsToSend[i].epassivationEasyfilm === '') {
				this.partsToSend.splice(i, 1);
			}
		}
		for (let i = 0; i < this.partsToSend.length; i++) {
			this.partsToSend[i].number = i + 1;
		}
		this.guarantee.parts = this.partsToSend;
	}

	async goToNextStep() {
		if (!this.canGoToNextStep()) {
			return;
		}
		this.saveParts();

		await this.router.navigate([
			this.guarantee.id !== undefined
				? `/guarantee-requests/edit/${ this.guarantee.id }/guarantee-request-confirmation`
				: '/guarantee-requests/new/guarantee-request-confirmation'
		], { replaceUrl: true });
	}

	async saveGuarantee(event: Event) {
		event.preventDefault();
		this.toggleSubSave(event);
		const now = moment().toISOString();
		const userName = `${ this.user.firstname } ${ this.user.lastname }`;
		const userId = this.user.id;
		if (this.guarantee.status !== GuaranteeStatus.DRAFT) {
			this.guarantee.requestDate = now;
		}
		this.guarantee.requesterName = userName;
		this.guarantee.requesterId = userId;
		this.saveParts();
		this.submitting = true;
		try {
			await this.guaranteeService.saveGuarantee(this.guarantee);
		} catch (err) {
			console.error(err);
			this.guarantee.status = GuaranteeStatus.DRAFT;
			this.snackBar.openFromComponent(
				SnackbarSaveDraftGuaranteeComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar'],
					data: true
				}
			);
			return;
		} finally {
			this.submitting = false;
		}
		if (this.guarantee.status === GuaranteeStatus.DRAFT) {
			await this.router.navigate(['/guarantee-requests/drafts'], { replaceUrl: true });
			this.snackBar.openFromComponent(
				SnackbarSaveDraftGuaranteeComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar']
				}
			);
		}
	}

	onMagnelisZM310Change(checked: boolean) {
		this.guarantee.magnelisZM310 = checked;
	}

	onMagnelisZM430Change(checked: boolean) {
		this.guarantee.magnelisZM430 = checked;
	}

	suggestedCoatingMassChanged(checked: boolean) {
		this.guarantee.suggestedCoatingMass = checked;
	}

	onMagnelisZM620Change(checked: boolean) {
		this.guarantee.magnelisZM620 = checked;
	}

	onPolesMagnelisZM430Change(checked: boolean) {
		this.guarantee.polesMagnelisZM430 = checked;
	}

	onPolesMagnelisZM620Change(checked: boolean) {
		this.guarantee.polesMagnelisZM620 = checked;
	}

	onPolesMagnelisZM800Change(checked: boolean) {
		this.guarantee.polesMagnelisZM800 = checked ? 'Yes' : 'No';
	}

	onEntireMagnelisChange(checked: boolean) {
		this.guarantee.entireMagnelisStructure = checked;
	}

	onStainlessSteelPartsChange(checked: boolean) {
		this.guarantee.stainlessSteelParts = checked;
	}

	onAluminiumPartsChange(checked: boolean) {
		this.guarantee.aluminiumParts = checked;
	}

	onFoundationsConcreteChange(checked: boolean) {
		this.guarantee.foundationsConcrete = checked;
	}

	onStainlessSteelChange(checked: boolean) {
		this.guarantee.stainlessSteel = checked;
	}

	onZincCoatedChange(checked: boolean) {
		this.guarantee.zincCoated = checked;
	}

	onOtherCoatingsSteelChange(checked: boolean) {
		this.guarantee.otherCoatingsSteel = checked;
	}

	onAluminiumChange(checked: boolean) {
		this.guarantee.aluminium = checked;
	}

	guaranteeDurationChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.guaranteeDurationRequested = target.value;
	}

	outdoorGuaranteeDurationChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.outdoorGuaranteeDurationExpected = target.value;
	}

	indoorGuaranteeDurationChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.indoorGuaranteeDurationExpected = target.value;
	}

	poleGuaranteeDurationChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.poleGuaranteeDurationRequested = target.value;
	}

	floatingPoleGuaranteeDurationChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.guaranteeDurationRequestedForFoating = target.value;
	}

	onMagnelisOtherChange(checked: boolean) {
		if (!checked) {
			this.guaranteeCoatingMassFormControl.reset();
			this.guarantee.otherCoatingMass = '';
		}
		this.guarantee.isOtherCoatingMass = checked;
	}

	guaranteeCoatingMassChanged(event: Event) {
		const target = <HTMLInputElement>event.target;
		this.guarantee.otherCoatingMass = target.value;
	}

	addAttachments(events: FileList | null) {
		this.attachmentError = buildAttachments(
			events,
			this.guarantee.attachments,
			this.translationService.getTR('guarantee.attachment.size'),
			`${ this.user.firstname } ${ this.user.lastname }`
		);
	}

	attachmentErrors(): string[] {
		return this.attachmentError ? [this.attachmentError] : [];
	}

	removeAttachmentEntity(attachment: GuaranteeRequestAttachment) {
		if (attachment.chronicleId) {
			attachment.deleted = true;
			return;
		}
		const index = this.guarantee.attachments.indexOf(attachment);
		if (index !== -1) {
			this.guarantee.attachments.splice(index, 1);
		}
	}

	activeAttachments(): GuaranteeRequestAttachment[] {
		return this.guarantee.attachments.filter(attachment => !attachment.deleted);
	}

	isSoilAnalysisRequired(): boolean {
		return this.guarantee.applicationType === 'Magnelis-Solar'
			&& this.polesGuaranteeFormControl.value
			&& this.soilAnalysisFormControl.value;
	}

	isWaterAnalysisRequired(): boolean {
		return this.guarantee.applicationType === 'Magnelis-Solar'
			&& this.guaranteeForFloatingSolarMagnelisFormControl.value === 'Yes'
			&& this.floatingSolarInstallationAnalysisFormControl.value === 'Yes';
	}

	hasSoilAnalysisAttachment(): boolean {
		return this.activeAttachments()
			.some(attachment => this.attachmentEditor.getCategory(attachment) === ATTACHMENT_CATEGORY_SOIL);
	}

	hasWaterAnalysisAttachment(): boolean {
		return this.activeAttachments()
			.some(attachment => this.attachmentEditor.getCategory(attachment) === ATTACHMENT_CATEGORY_WATER);
	}

	isSoilDocMissing(): boolean {
		return this.isSoilAnalysisRequired() && !this.hasSoilAnalysisAttachment();
	}

	isWaterDocMissing(): boolean {
		return this.isWaterAnalysisRequired() && !this.hasWaterAnalysisAttachment();
	}

	hasAnyMissingCategory(): boolean {
		return this.isSoilDocMissing() || this.isWaterDocMissing();
	}

	canGoToNextStep(): boolean {
		return !this.attachmentEditor.updating && !this.hasAnyMissingCategory();
	}

	formatSize(attachment: GuaranteeRequestAttachment): string {
		return formatAttachmentSize(attachment.size);
	}

	getAttachmentUrl(id: string | undefined, attachment: GuaranteeRequestAttachment) {
		if (attachment.localUrl) {
			return attachment.localUrl;
		}
		if (!id || !attachment.chronicleId) {
			return '';
		}
		return this.guaranteeService.getDownloadFile(safe(id), safe(attachment.chronicleId));
	}

	async showAttachment(event: MouseEvent, attachment: GuaranteeRequestAttachment) {
		await this.guaranteeService.showAttachment(event, attachment);
	}

	onUpperStructureGuarantee(event: MatRadioChange) {
		this.guarantee.upperStructureGuarantee = Boolean(event.value);
	}

	onPolesGuarantee(event: MatRadioChange) {
		this.guarantee.polesGuarantee = Boolean(event.value);
		if (event.value === false) {
			this.polesMagnelisZM430FormControl.reset(false);
			this.polesMagnelisZM620FormControl.reset(false);
			this.polesMagnelisZM800FormControl.reset('');
			this.poleGuaranteeDurationFormControl.reset('');
			this.guarantee.polesMagnelisZM430 = false;
			this.guarantee.polesMagnelisZM620 = false;
			this.guarantee.polesMagnelisZM800 = '';
			this.guarantee.poleGuaranteeDurationRequested = '';
			this.attachmentEditor.demoteCategoryToOther(
				this.guarantee,
				this.activeAttachments(),
				ATTACHMENT_CATEGORY_SOIL
			);
		}
	}

	onGuaranteeForFloatingSolarMagnelis(event: MatRadioChange) {
		this.guarantee.guaranteeForFloatingSolarMagnelis = String(event.value);
		if (event.value !== 'Yes') {
			this.floatingSolarMagnelisZM430FormControl.reset('');
			this.floatingSolarMagnelisZM620FormControl.reset('');
			this.floatingSolarMagnelisZM800FormControl.reset('');
			this.floatingPoleGuaranteeDurationFormControl.reset('');
			this.guarantee.floatingSolarMagnelisZM430 = '';
			this.guarantee.floatingSolarMagnelisZM620 = '';
			this.guarantee.floatingSolarMagnelisZM800 = '';
			this.guarantee.guaranteeDurationRequestedForFoating = '';
			this.attachmentEditor.demoteCategoryToOther(
				this.guarantee,
				this.activeAttachments(),
				ATTACHMENT_CATEGORY_WATER
			);
		}
	}

	onFloatingSolarMagnelisZM430Change(checked: boolean) {
		this.guarantee.floatingSolarMagnelisZM430 = checked ? 'Yes' : 'No';
	}

	onFloatingSolarMagnelisZM620Change(checked: boolean) {
		this.guarantee.floatingSolarMagnelisZM620 = checked ? 'Yes' : 'No';
	}

	onFloatingSolarMagnelisZM800Change(checked: boolean) {
		this.guarantee.floatingSolarMagnelisZM800 = checked ? 'Yes' : 'No';
	}

	onSoilAnalysisChange(event: MatRadioChange) {
		this.guarantee.soilAnalysis = Boolean(event.value);
		if (event.value === false) {
			this.attachmentEditor.demoteCategoryToOther(
				this.guarantee,
				this.activeAttachments(),
				ATTACHMENT_CATEGORY_SOIL
			);
		}
	}

	onFloatingSolarInstallationAnalysisChange(event: MatRadioChange) {
		this.guarantee.floatingSolarInstallationAnalysis = String(event.value);
		if (event.value !== 'Yes') {
			this.attachmentEditor.demoteCategoryToOther(
				this.guarantee,
				this.activeAttachments(),
				ATTACHMENT_CATEGORY_WATER
			);
		}
	}

	onMagnelisContactPartsCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.magnelisContactPartsCoating = target.value;
	}

	onMagnelisContactPartsOtherMaterialChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.magnelisContactPartsOtherMaterial = target.value;
	}

	onCustomerAdditionalInformationChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.customerAdditionalInformation = target.value;
	}

	contactPartsChanged(checked: boolean) {
		if (!checked) {
			this.magnelisContactPartsCoatingFormControl.reset('');
			this.guarantee.magnelisContactPartsCoating = '';
		}
	}

	contactPartsOtherChanged(checked: boolean) {
		if (!checked) {
			this.magnelisContactPartsOtherMaterialFormControl.reset('');
			this.guarantee.magnelisContactPartsOtherMaterial = '';
		}
	}

	checkLength(event: Event) {
		const target = <HTMLInputElement> event.target;

		return target.value.length !== 5;
	}

	checkPasteLength(event: ClipboardEvent) {
		if (event.clipboardData) {
			const pastedNumber = event.clipboardData.getData('text');
			if (pastedNumber.length > 5) {
				return false;
			}
		}

		return true;
	}

	typeOfGuaGrOutdoorCheckboxChanged(checked: boolean) {
		this.guarantee.typeOfGuaGrOutdoor = checked;
		if (!checked) {
			this.guarantee.outdoorGuaranteeDurationExpected = '';
			this.outdoorGuaranteeDurationFormControl.setValue('');
		}
	}

	typeofGuaGrIndoorCheckboxChanged(checked: boolean) {
		this.guarantee.typeofGuaGrIndoor = checked;
		if (!checked) {
			this.guarantee.indoorGuaranteeDurationExpected = '';
			this.indoorGuaranteeDurationFormControl.setValue('');
		}
	}

	typeOfGUAOpenBuildingYesChanged(event: MatRadioChange) {
		this.guarantee.typeOfGUAOpenBuildingYes = Boolean(event.value);
		if (event.value === false) {
			this.typeOfGUAOpenBuildingYesSpecifyFormControl.reset('');
			this.guarantee.typeOfGUAOpenBuildingYesSpecify = '';
		}
	}

	typeOfGUAOpenBuildingYesSpecifyChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.typeOfGUAOpenBuildingYesSpecify = target.value;
	}

	ocRoofingCheckboxChange(checked: boolean) {
		this.guarantee.ocRoofing = checked;
		if (!checked) {
			this.roofingDeckingRoofFormControl.reset(false);
			this.roofingDRSubstrateThicknessFormControl.reset('');
			this.roofingDRDesiredCoatingFormControl.reset('');
			this.roofingDRDesiredBackCoatingFormControl.reset('');
			this.roofingDRMetallicCoatingWeightFormControl.reset('');
			this.roofingDRColorFormControl.reset('');
			this.roofingDRSurfaceFormControl.reset('');
			this.roofingDRRoofslopeFormControl.reset('');
			this.roofingSingleSkinRoofFormControl.reset(false);
			this.roofingSSRSubstrateThicknessFormControl.reset('');
			this.roofingSSRDesiredCoatingFormControl.reset('');
			this.roofingSSRDesiredBackCoatingFormControl.reset('');
			this.roofingSSRMetallicCoatingWeightFormControl.reset('');
			this.roofingSSRColorFormControl.reset('');
			this.roofingSSRSurfaceFormControl.reset('');
			this.roofingSSRRoofslopeFormControl.reset('');
			this.roofingDoubleSkinRoofFormControl.reset(false);
			this.roofingDsresSubstrateThicknessFormControl.reset('');
			this.roofingDsresDesiredCoatingFormControl.reset('');
			this.roofingDsresDesiredBackCoatingFormControl.reset('');
			this.roofingDsresMetallicCoatingWeightFormControl.reset('');
			this.roofingDsrEsColorFormControl.reset('');
			this.roofingDsrisSubstrateThicknessFormControl.reset('');
			this.roofingDsrisDesiredCoatingFormControl.reset('');
			this.roofingDsrisDesiredBackCoatingFormControl.reset('');
			this.roofingDsrisMetallicCoatingWeightFormControl.reset('');
			this.roofingDsrIsColorFormControl.reset('');
			this.roofingDSRSurfaceFormControl.reset('');
			this.roofingDSRRoofslopeFormControl.reset('');
			this.roofingSandwichPanelsBuiltUpSysFormControl.reset(false);
			this.roofingSPESSubstrateThicknessFormControl.reset('');
			this.roofingSPESDesiredCoatingFormControl.reset('');
			this.roofingSPESDesiredBackCoatingFormControl.reset('');
			this.roofingSPESMetallicCoatingWeightFormControl.reset('');
			this.roofingSPESColorFormControl.reset('');
			this.roofingSPISSubstrateThicknessFormControl.reset('');
			this.roofingSPISDesiredCoatingFormControl.reset('');
			this.roofingSPISDesiredBackCoatingFormControl.reset('');
			this.roofingSPISMetallicCoatingWeightFormControl.reset('');
			this.roofingSPISColorFormControl.reset('');
			this.roofingSPSurfaceFormControl.reset('');
			this.roofingSPRoofslopeFormControl.reset('');
			this.roofingSPInsulationPUFormControl.reset(false);
			this.roofingSPInsulationPIRFormControl.reset(false);
			this.roofingSPInsulationMineralWoolFormControl.reset(false);
			this.roofingOverlappingFormControl.reset('');
			this.roofingPerforationYesFormControl.reset('');
			this.roofingPerforationYesSpecifyFormControl.reset('');
			this.roofingPVSolarRoofFormControl.reset('');
			this.roofingAntiCondensationFeltFormControl.reset('');
			this.guarantee.roofingDeckingRoof = false;
			this.guarantee.roofingDRSubstrateThickness = '';
			this.guarantee.roofingDRDesiredCoating = '';
			this.guarantee.roofingDRDesiredBackCoating = '';
			this.guarantee.roofingDRMetallicCoatingWeight = '';
			this.guarantee.roofingDRColor = '';
			this.guarantee.roofingDRSurface = '';
			this.guarantee.roofingDRRoofslope = '';
			this.guarantee.roofingSingleSkinRoof = false;
			this.guarantee.roofingSSRSubstrateThickness = '';
			this.guarantee.roofingSSRDesiredCoating = '';
			this.guarantee.roofingSSRDesiredBackCoating = '';
			this.guarantee.roofingSSRMetallicCoatingWeight = '';
			this.guarantee.roofingSSRColor = '';
			this.guarantee.roofingSSRSurface = '';
			this.guarantee.roofingSSRRoofslope = '';
			this.guarantee.roofingDoubleSkinRoof = false;
			this.guarantee.roofingDsresSubstrateThickness = '';
			this.guarantee.roofingDsresDesiredCoating = '';
			this.guarantee.roofingDsresDesiredBackCoating = '';
			this.guarantee.roofingDsresMetallicCoatingWeight = '';
			this.guarantee.roofingDsrEsColor = '';
			this.guarantee.roofingDsrisSubstrateThickness = '';
			this.guarantee.roofingDsrisDesiredCoating = '';
			this.guarantee.roofingDsrisDesiredBackCoating = '';
			this.guarantee.roofingDsrisMetallicCoatingWeight = '';
			this.guarantee.roofingDsrIsColor = '';
			this.guarantee.roofingDSRSurface = '';
			this.guarantee.roofingDSRRoofslope = '';
			this.guarantee.roofingSandwichPanelsBuiltUpSys = false;
			this.guarantee.roofingSPESSubstrateThickness = '';
			this.guarantee.roofingSPESDesiredCoating = '';
			this.guarantee.roofingSPESDesiredBackCoating = '';
			this.guarantee.roofingSPESMetallicCoatingWeight = '';
			this.guarantee.roofingSPESColor = '';
			this.guarantee.roofingSPISSubstrateThickness = '';
			this.guarantee.roofingSPISDesiredCoating = '';
			this.guarantee.roofingSPISDesiredBackCoating = '';
			this.guarantee.roofingSPISMetallicCoatingWeight = '';
			this.guarantee.roofingSPISColor = '';
			this.guarantee.roofingSPSurface = '';
			this.guarantee.roofingSPRoofslope = '';
			this.guarantee.roofingSPInsulationPU = false;
			this.guarantee.roofingSPInsulationPIR = false;
			this.guarantee.roofingSPInsulationMineralWool = false;
			this.guarantee.roofingOverlapping = '';
			this.guarantee.roofingPerforationYes = '';
			this.guarantee.roofingPerforationYesSpecify = '';
			this.guarantee.roofingPVSolarRoof = '';
			this.guarantee.roofingAntiCondensationFelt = '';
		}
	}

	roofingDeckingRoofCheckboxChange(checked: boolean) {
		this.guarantee.roofingDeckingRoof = checked;
		if (!checked) {
			this.roofingDRSubstrateThicknessFormControl.reset('');
			this.roofingDRDesiredCoatingFormControl.reset('');
			this.roofingDRDesiredBackCoatingFormControl.reset('');
			this.roofingDRMetallicCoatingWeightFormControl.reset('');
			this.roofingDRColorFormControl.reset('');
			this.roofingDRSurfaceFormControl.reset('');
			this.roofingDRRoofslopeFormControl.reset('');
			this.guarantee.roofingDRSubstrateThickness = '';
			this.guarantee.roofingDRDesiredCoating = '';
			this.guarantee.roofingDRDesiredBackCoating = '';
			this.guarantee.roofingDRMetallicCoatingWeight = '';
			this.guarantee.roofingDRColor = '';
			this.guarantee.roofingDRSurface = '';
			this.guarantee.roofingDRRoofslope = '';
		}
	}

	roofingDRSubstrateThicknessChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDRSubstrateThickness = target.value;
	}

	roofingDRDesiredCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDRDesiredCoating = target.value;
	}

	roofingDRDesiredBackCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDRDesiredBackCoating = target.value;
	}

	roofingDRMetallicCoatingWeightChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDRMetallicCoatingWeight = target.value;
	}

	roofingDRColorChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDRColor = target.value;
	}

	roofingDRSurfaceChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDRSurface = target.value;
	}

	roofingDRRoofslopeChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDRRoofslope = target.value;
	}

	roofingSingleSkinRoofCheckboxChange(checked: boolean) {
		this.guarantee.roofingSingleSkinRoof = checked;
		if (!checked) {
			this.roofingSSRSubstrateThicknessFormControl.reset('');
			this.roofingSSRDesiredCoatingFormControl.reset('');
			this.roofingSSRDesiredBackCoatingFormControl.reset('');
			this.roofingSSRMetallicCoatingWeightFormControl.reset('');
			this.roofingSSRColorFormControl.reset('');
			this.roofingSSRSurfaceFormControl.reset('');
			this.roofingSSRRoofslopeFormControl.reset('');
			this.guarantee.roofingSSRSubstrateThickness = '';
			this.guarantee.roofingSSRDesiredCoating = '';
			this.guarantee.roofingSSRDesiredBackCoating = '';
			this.guarantee.roofingSSRMetallicCoatingWeight = '';
			this.guarantee.roofingSSRColor = '';
			this.guarantee.roofingSSRSurface = '';
			this.guarantee.roofingSSRRoofslope = '';
		}
	}

	roofingSSRSubstrateThicknessChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSSRSubstrateThickness = target.value;
	}

	roofingSSRDesiredCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSSRDesiredCoating = target.value;
	}

	roofingSSRDesiredBackCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSSRDesiredBackCoating = target.value;
	}

	roofingSSRMetallicCoatingWeightChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSSRMetallicCoatingWeight = target.value;
	}

	roofingSSRColorChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSSRColor = target.value;
	}

	roofingSSRSurfaceChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSSRSurface = target.value;
	}

	roofingSSRRoofslopeChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSSRRoofslope = target.value;
	}

	roofingDoubleSkinRoofCheckboxChange(checked: boolean) {
		this.guarantee.roofingDoubleSkinRoof = checked;
		if (!checked) {
			this.roofingDsresSubstrateThicknessFormControl.reset('');
			this.roofingDsresDesiredCoatingFormControl.reset('');
			this.roofingDsresDesiredBackCoatingFormControl.reset('');
			this.roofingDsresMetallicCoatingWeightFormControl.reset('');
			this.roofingDsrEsColorFormControl.reset('');
			this.roofingDsrisSubstrateThicknessFormControl.reset('');
			this.roofingDsrisDesiredCoatingFormControl.reset('');
			this.roofingDsrisDesiredBackCoatingFormControl.reset('');
			this.roofingDsrisMetallicCoatingWeightFormControl.reset('');
			this.roofingDsrIsColorFormControl.reset('');
			this.roofingDSRSurfaceFormControl.reset('');
			this.roofingDSRRoofslopeFormControl.reset('');
			this.guarantee.roofingDsresSubstrateThickness = '';
			this.guarantee.roofingDsresDesiredCoating = '';
			this.guarantee.roofingDsresDesiredBackCoating = '';
			this.guarantee.roofingDsresMetallicCoatingWeight = '';
			this.guarantee.roofingDsrEsColor = '';
			this.guarantee.roofingDsrisSubstrateThickness = '';
			this.guarantee.roofingDsrisDesiredCoating = '';
			this.guarantee.roofingDsrisDesiredBackCoating = '';
			this.guarantee.roofingDsrisMetallicCoatingWeight = '';
			this.guarantee.roofingDsrIsColor = '';
			this.guarantee.roofingDSRSurface = '';
			this.guarantee.roofingDSRRoofslope = '';
		}
	}

	roofingDsresSubstrateThicknessChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDsresSubstrateThickness = target.value;
	}

	roofingDsresDesiredCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDsresDesiredCoating = target.value;
	}

	roofingDsresDesiredBackCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDsresDesiredBackCoating = target.value;
	}

	roofingDsresMetallicCoatingWeightChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDsresMetallicCoatingWeight = target.value;
	}

	roofingDsrEsColorChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDsrEsColor = target.value;
	}

	roofingDsrisSubstrateThicknessChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDsrisSubstrateThickness = target.value;
	}

	roofingDsrisDesiredCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDsrisDesiredCoating = target.value;
	}

	roofingDsrisDesiredBackCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDsrisDesiredBackCoating = target.value;
	}

	roofingDsrisMetallicCoatingWeightChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDsrisMetallicCoatingWeight = target.value;
	}

	roofingDsrIsColorChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDsrIsColor = target.value;
	}

	roofingDSRSurfaceChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDSRSurface = target.value;
	}

	roofingDSRRoofslopeChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingDSRRoofslope = target.value;
	}

	roofingSandwichPanelsBuiltUpSysCheckboxChange(checked: boolean) {
		this.guarantee.roofingSandwichPanelsBuiltUpSys = checked;
		if (!checked) {
			this.roofingSPESSubstrateThicknessFormControl.reset('');
			this.roofingSPESDesiredCoatingFormControl.reset('');
			this.roofingSPESDesiredBackCoatingFormControl.reset('');
			this.roofingSPESMetallicCoatingWeightFormControl.reset('');
			this.roofingSPESColorFormControl.reset('');
			this.roofingSPISSubstrateThicknessFormControl.reset('');
			this.roofingSPISDesiredCoatingFormControl.reset('');
			this.roofingSPISDesiredBackCoatingFormControl.reset('');
			this.roofingSPISMetallicCoatingWeightFormControl.reset('');
			this.roofingSPISColorFormControl.reset('');
			this.roofingSPSurfaceFormControl.reset('');
			this.roofingSPRoofslopeFormControl.reset('');
			this.roofingSPInsulationPUFormControl.reset(false);
			this.roofingSPInsulationPIRFormControl.reset(false);
			this.roofingSPInsulationMineralWoolFormControl.reset(false);
			this.guarantee.roofingSPESSubstrateThickness = '';
			this.guarantee.roofingSPESDesiredCoating = '';
			this.guarantee.roofingSPESDesiredBackCoating = '';
			this.guarantee.roofingSPESMetallicCoatingWeight = '';
			this.guarantee.roofingSPESColor = '';
			this.guarantee.roofingSPISSubstrateThickness = '';
			this.guarantee.roofingSPISDesiredCoating = '';
			this.guarantee.roofingSPISDesiredBackCoating = '';
			this.guarantee.roofingSPISMetallicCoatingWeight = '';
			this.guarantee.roofingSPISColor = '';
			this.guarantee.roofingSPSurface = '';
			this.guarantee.roofingSPRoofslope = '';
			this.guarantee.roofingSPInsulationPU = false;
			this.guarantee.roofingSPInsulationPIR = false;
			this.guarantee.roofingSPInsulationMineralWool = false;
		}
	}

	roofingSPESSubstrateThicknessChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPESSubstrateThickness = target.value;
	}

	roofingSPESDesiredCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPESDesiredCoating = target.value;
	}

	roofingSPESDesiredBackCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPESDesiredBackCoating = target.value;
	}

	roofingSPESMetallicCoatingWeightChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPESMetallicCoatingWeight = target.value;
	}

	roofingSPESColorChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPESColor = target.value;
	}

	roofingSPISSubstrateThicknessChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPISSubstrateThickness = target.value;
	}

	roofingSPISDesiredCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPISDesiredCoating = target.value;
	}

	roofingSPISDesiredBackCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPISDesiredBackCoating = target.value;
	}

	roofingSPISMetallicCoatingWeightChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPISMetallicCoatingWeight = target.value;
	}

	roofingSPISColorChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPISColor = target.value;
	}

	roofingSPSurfaceChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPSurface = target.value;
	}

	roofingSPRoofslopeChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingSPRoofslope = target.value;
	}

	roofingSPInsulationPUCheckboxChange(checked: boolean) {
		this.guarantee.roofingSPInsulationPU = checked;
	}

	roofingSPInsulationPIRCheckboxChange(checked: boolean) {
		this.guarantee.roofingSPInsulationPIR = checked;
	}

	roofingSPInsulationMineralWoolCheckboxChange(checked: boolean) {
		this.guarantee.roofingSPInsulationMineralWool = checked;
	}

	roofingOverlappingChanged(event: MatRadioChange) {
		this.guarantee.roofingOverlapping = String(event.value);
	}

	roofingPerforationYesChanged(event: MatRadioChange) {
		this.guarantee.roofingPerforationYes = String(event.value);
		if (event.value === false) {
			this.roofingPerforationYesSpecifyFormControl.reset('');
			this.guarantee.roofingPerforationYesSpecify = '';
		}
	}

	roofingPerforationYesSpecifyChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.roofingPerforationYesSpecify = target.value;
	}

	roofingPVSolarRoofChanged(event: MatRadioChange) {
		this.guarantee.roofingPVSolarRoof = String(event.value);
	}

	roofingAntiCondensationFeltChanged(event: MatRadioChange) {
		this.guarantee.roofingAntiCondensationFelt = String(event.value);
	}

	cladCheckboxChange(checked: boolean) {
		this.guarantee.clad = checked;
		if (!checked) {
			this.cladSingleSkinFormControl.reset(false);
			this.cladSSSubstrateThicknessFormControl.reset('');
			this.cladSSDesiredCoatingFormControl.reset('');
			this.cladSSDesiredBackCoatingFormControl.reset('');
			this.cladSSMetallicCoatingWeightFormControl.reset('');
			this.cladSSColorFormControl.reset('');
			this.cladSSSurfaceFormControl.reset('');
			this.cladDoubleSkinFormControl.reset(false);
			this.cladDSESSubstrateThicknessFormControl.reset('');
			this.cladDSESDesiredCoatingFormControl.reset('');
			this.cladDSESDesiredBackCoatingFormControl.reset('');
			this.cladDSESMetallicCoatingWeightFormControl.reset('');
			this.cladDSESColorFormControl.reset('');
			this.cladDSESSurfaceFormControl.reset('');
			this.cladDSISSubstrateThicknessFormControl.reset('');
			this.cladDSISDesiredCoatingFormControl.reset('');
			this.cladDSISDesiredBackCoatingFormControl.reset('');
			this.cladDSISMetallicCoatingWeightFormControl.reset('');
			this.cladDSISColorFormControl.reset('');
			this.cladDSISSurfaceFormControl.reset('');
			this.cladSandwichPanelsBuiltUpSysFormControl.reset(false);
			this.cladSPESSubstrateThicknessFormControl.reset('');
			this.cladSPESDesiredCoatingFormControl.reset('');
			this.cladSPESDesiredBackCoatingFormControl.reset('');
			this.cladSPESMetallicCoatingWeightFormControl.reset('');
			this.cladSPESColorFormControl.reset('');
			this.cladSPISSubstrateThicknessFormControl.reset('');
			this.cladSPISDesiredCoatingFormControl.reset('');
			this.cladSPISDesiredBackCoatingFormControl.reset('');
			this.cladSPISMetallicCoatingWeightFormControl.reset('');
			this.cladSPISColorFormControl.reset('');
			this.cladSPSurfaceFormControl.reset('');
			this.cladSPInsulationPUFormControl.reset(false);
			this.cladSPInsulationPIRFormControl.reset(false);
			this.cladSPInsulationMineralWoolFormControl.reset(false);
			this.cladOverlappingFormControl.reset('');
			this.cladPerforationYesFormControl.reset('');
			this.cladPerforationYesSpecifyFormControl.reset('');
			this.cladDirectionOfInstallationFormControl.reset(false);
			this.cladVerticalFormControl.reset(false);
			this.guarantee.cladSingleSkin = false;
			this.guarantee.cladSSSubstrateThickness = '';
			this.guarantee.cladSSDesiredCoating = '';
			this.guarantee.cladSSDesiredBackCoating = '';
			this.guarantee.cladSSMetallicCoatingWeight = '';
			this.guarantee.cladSSColor = '';
			this.guarantee.cladSSSurface = '';
			this.guarantee.cladDoubleSkin = false;
			this.guarantee.cladDSESSubstrateThickness = '';
			this.guarantee.cladDSESDesiredCoating = '';
			this.guarantee.cladDSESDesiredBackCoating = '';
			this.guarantee.cladDSESMetallicCoatingWeight = '';
			this.guarantee.cladDSESColor = '';
			this.guarantee.cladDSESSurface = '';
			this.guarantee.cladDSISSubstrateThickness = '';
			this.guarantee.cladDSISDesiredCoating = '';
			this.guarantee.cladDSISDesiredBackCoating = '';
			this.guarantee.cladDSISMetallicCoatingWeight = '';
			this.guarantee.cladDSISColor = '';
			this.guarantee.cladDSISSurface = '';
			this.guarantee.cladSandwichPanelsBuiltUpSys = false;
			this.guarantee.cladSPESSubstrateThickness = '';
			this.guarantee.cladSPESDesiredCoating = '';
			this.guarantee.cladSPESDesiredBackCoating = '';
			this.guarantee.cladSPESMetallicCoatingWeight = '';
			this.guarantee.cladSPESColor = '';
			this.guarantee.cladSPISSubstrateThickness = '';
			this.guarantee.cladSPISDesiredCoating = '';
			this.guarantee.cladSPISDesiredBackCoating = '';
			this.guarantee.cladSPISMetallicCoatingWeight = '';
			this.guarantee.cladSPISColor = '';
			this.guarantee.cladSPSurface = '';
			this.guarantee.cladSPInsulationPU = false;
			this.guarantee.cladSPInsulationPIR = false;
			this.guarantee.cladSPInsulationMineralWool = false;
			this.guarantee.cladOverlapping = '';
			this.guarantee.cladPerforationYes = '';
			this.guarantee.cladPerforationYesSpecify = '';
			this.guarantee.cladDirectionOfInstallation = false;
			this.guarantee.cladVertical = false;
		}
	}

	cladSingleSkinCheckboxChange(checked: boolean) {
		this.guarantee.cladSingleSkin = checked;
		if (!checked) {
			this.cladSSSubstrateThicknessFormControl.reset('');
			this.cladSSDesiredCoatingFormControl.reset('');
			this.cladSSDesiredBackCoatingFormControl.reset('');
			this.cladSSMetallicCoatingWeightFormControl.reset('');
			this.cladSSColorFormControl.reset('');
			this.cladSSSurfaceFormControl.reset('');
			this.guarantee.cladSSSubstrateThickness = '';
			this.guarantee.cladSSDesiredCoating = '';
			this.guarantee.cladSSDesiredBackCoating = '';
			this.guarantee.cladSSMetallicCoatingWeight = '';
			this.guarantee.cladSSColor = '';
			this.guarantee.cladSSSurface = '';
		}
	}

	cladSSSubstrateThicknessChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSSSubstrateThickness = target.value;
	}

	cladSSDesiredCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSSDesiredCoating = target.value;
	}

	cladSSDesiredBackCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSSDesiredBackCoating = target.value;
	}

	cladSSMetallicCoatingWeightChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSSMetallicCoatingWeight = target.value;
	}

	cladSSColorChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSSColor = target.value;
	}

	cladSSSurfaceChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSSSurface = target.value;
	}

	cladDoubleSkinCheckboxChange(checked: boolean) {
		this.guarantee.cladDoubleSkin = checked;
		if (!checked) {
			this.cladDSESSubstrateThicknessFormControl.reset('');
			this.cladDSESDesiredCoatingFormControl.reset('');
			this.cladDSESDesiredBackCoatingFormControl.reset('');
			this.cladDSESMetallicCoatingWeightFormControl.reset('');
			this.cladDSESColorFormControl.reset('');
			this.cladDSESSurfaceFormControl.reset('');
			this.cladDSISSubstrateThicknessFormControl.reset('');
			this.cladDSISDesiredCoatingFormControl.reset('');
			this.cladDSISDesiredBackCoatingFormControl.reset('');
			this.cladDSISMetallicCoatingWeightFormControl.reset('');
			this.cladDSISColorFormControl.reset('');
			this.cladDSISSurfaceFormControl.reset('');
			this.guarantee.cladDSESSubstrateThickness = '';
			this.guarantee.cladDSESDesiredCoating = '';
			this.guarantee.cladDSESDesiredBackCoating = '';
			this.guarantee.cladDSESMetallicCoatingWeight = '';
			this.guarantee.cladDSESColor = '';
			this.guarantee.cladDSESSurface = '';
			this.guarantee.cladDSISSubstrateThickness = '';
			this.guarantee.cladDSISDesiredCoating = '';
			this.guarantee.cladDSISDesiredBackCoating = '';
			this.guarantee.cladDSISMetallicCoatingWeight = '';
			this.guarantee.cladDSISColor = '';
			this.guarantee.cladDSISSurface = '';
		}
	}

	cladDSESSubstrateThicknessChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSESSubstrateThickness = target.value;
	}

	cladDSESDesiredCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSESDesiredCoating = target.value;
	}

	cladDSESDesiredBackCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSESDesiredBackCoating = target.value;
	}

	cladDSESMetallicCoatingWeightChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSESMetallicCoatingWeight = target.value;
	}

	cladDSESColorChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSESColor = target.value;
	}

	cladDSESSurfaceChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSESSurface = target.value;
	}

	cladDSISSubstrateThicknessChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSISSubstrateThickness = target.value;
	}

	cladDSISDesiredCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSISDesiredCoating = target.value;
	}

	cladDSISDesiredBackCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSISDesiredBackCoating = target.value;
	}

	cladDSISMetallicCoatingWeightChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSISMetallicCoatingWeight = target.value;
	}

	cladDSISColorChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSISColor = target.value;
	}

	cladDSISSurfaceChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladDSISSurface = target.value;
	}

	cladSandwichPanelsBuiltUpSysCheckboxChange(checked: boolean) {
		this.guarantee.cladSandwichPanelsBuiltUpSys = checked;
		if (!checked) {
			this.cladSPESSubstrateThicknessFormControl.reset('');
			this.cladSPESDesiredCoatingFormControl.reset('');
			this.cladSPESDesiredBackCoatingFormControl.reset('');
			this.cladSPESMetallicCoatingWeightFormControl.reset('');
			this.cladSPESColorFormControl.reset('');
			this.cladSPISSubstrateThicknessFormControl.reset('');
			this.cladSPISDesiredCoatingFormControl.reset('');
			this.cladSPISDesiredBackCoatingFormControl.reset('');
			this.cladSPISMetallicCoatingWeightFormControl.reset('');
			this.cladSPISColorFormControl.reset('');
			this.cladSPSurfaceFormControl.reset('');
			this.cladSPInsulationPUFormControl.reset(false);
			this.cladSPInsulationPIRFormControl.reset(false);
			this.cladSPInsulationMineralWoolFormControl.reset(false);
			this.guarantee.cladSPESSubstrateThickness = '';
			this.guarantee.cladSPESDesiredCoating = '';
			this.guarantee.cladSPESDesiredBackCoating = '';
			this.guarantee.cladSPESMetallicCoatingWeight = '';
			this.guarantee.cladSPESColor = '';
			this.guarantee.cladSPISSubstrateThickness = '';
			this.guarantee.cladSPISDesiredCoating = '';
			this.guarantee.cladSPISDesiredBackCoating = '';
			this.guarantee.cladSPISMetallicCoatingWeight = '';
			this.guarantee.cladSPISColor = '';
			this.guarantee.cladSPSurface = '';
			this.guarantee.cladSPInsulationPU = false;
			this.guarantee.cladSPInsulationPIR = false;
			this.guarantee.cladSPInsulationMineralWool = false;
		}
	}

	cladSPESSubstrateThicknessChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSPESSubstrateThickness = target.value;
	}

	cladSPESDesiredCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSPESDesiredCoating = target.value;
	}

	cladSPESDesiredBackCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSPESDesiredBackCoating = target.value;
	}

	cladSPESMetallicCoatingWeightChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSPESMetallicCoatingWeight = target.value;
	}

	cladSPESColorChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSPESColor = target.value;
	}

	cladSPISSubstrateThicknessChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSPISSubstrateThickness = target.value;
	}

	cladSPISDesiredCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSPISDesiredCoating = target.value;
	}

	cladSPISDesiredBackCoatingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSPISDesiredBackCoating = target.value;
	}

	cladSPISMetallicCoatingWeightChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSPISMetallicCoatingWeight = target.value;
	}

	cladSPISColorChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSPISColor = target.value;
	}

	cladSPSurfaceChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladSPSurface = target.value;
	}

	cladSPInsulationPUCheckboxChange(checked: boolean) {
		this.guarantee.cladSPInsulationPU = checked;
	}

	cladSPInsulationPIRCheckboxChange(checked: boolean) {
		this.guarantee.cladSPInsulationPIR = checked;
	}

	cladSPInsulationMineralWoolCheckboxChange(checked: boolean) {
		this.guarantee.cladSPInsulationMineralWool = checked;
	}

	cladOverlappingChanged(event: MatRadioChange) {
		this.guarantee.cladOverlapping = String(event.value);
	}

	cladPerforationYesChanged(event: MatRadioChange) {
		this.guarantee.cladPerforationYes = String(event.value);
		if (event.value === 'false' || event.value === 'true' || event.value === '') {
			this.cladPerforationYesSpecifyFormControl.reset('');
			this.guarantee.cladPerforationYesSpecify = '';
		}
	}

	cladPerforationYesSpecifyChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.cladPerforationYesSpecify = target.value;
	}

	cladDirectionOfInstallationCheckboxChanged(checked: boolean) {
		this.guarantee.cladDirectionOfInstallation = checked;
	}

	cladVerticalCheckboxChanged(checked: boolean) {
		this.guarantee.cladVertical = checked;
	}

	othersAntiCondensationFeltCheckboxChange(checked: boolean) {
		this.guarantee.othersAntiCondensationFelt = checked;
		if (!checked) {
			this.othersSpecifyAntiCondensationFeltFormControl.reset('');
			this.guarantee.othersSpecifyAntiCondensationFelt = '';
		}
	}

	othersSpecifyAntiCondensationFeltChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.othersSpecifyAntiCondensationFelt = target.value;
	}

	omuRoofCladmadeWithProductCheckboxChange(checked: boolean) {
		this.guarantee.omuRoofCladmadeWithProduct = checked;
	}

	omuUseofMaterialCementBreezeblocksCheckboxChange(checked: boolean) {
		this.guarantee.omuUseofMaterialCementBreezeblocks = checked;
	}

	omuInRoofingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.omuInRoofing = target.value;
	}

	omuInCladdingChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.omuInCladding = target.value;
	}

	getLongType() {
		switch (this.guarantee.applicationType) {
		case 'Magnelis-Solar':
			return ` - ${ this.translationService.getTR('guarantee.magnelissolar.long') }`;
		case 'Magnelis-Building':
			return ` - ${ this.translationService.getTR('guarantee.magnelisbuilding.long') }`;
		case 'Magnelis-Other application':
			return ` - ${ this.translationService.getTR('guarantee.magnelisothers.long') }`;
		case 'Organic Coating':
			return ` - ${ this.translationService.getTR('guarantee.organiccoating.long') }`;
		}
		return '';
	}
}
